import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface Box {
  id: string;
  box_number: string;
  name: string;
  location_status: string;
  storage_start_date: string | null;
  stored_at: string | null;
}

interface AdminBoxEditDialogProps {
  box: Box | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export const AdminBoxEditDialog = ({ box, open, onOpenChange, onSaved }: AdminBoxEditDialogProps) => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [locationStatus, setLocationStatus] = useState("");
  const [storageStartDate, setStorageStartDate] = useState("");
  const [storedAt, setStoredAt] = useState("");

  // Reset form when box changes
  const resetForm = () => {
    if (box) {
      setName(box.name);
      setLocationStatus(box.location_status);
      setStorageStartDate(box.storage_start_date ? box.storage_start_date.split('T')[0] : "");
      setStoredAt(box.stored_at ? box.stored_at.split('T')[0] : "");
    }
  };

  // Reset form when box changes or dialog opens
  useEffect(() => {
    if (open && box) {
      resetForm();
    }
  }, [box, open]);

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      resetForm();
    }
    onOpenChange(isOpen);
  };

  const handleSave = async () => {
    if (!box) return;

    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Error",
          description: "You must be logged in",
          variant: "destructive",
        });
        return;
      }

      const updates: Record<string, any> = {
        name,
        location_status: locationStatus,
      };

      // Only include dates if they have values
      if (storageStartDate) {
        updates.storage_start_date = new Date(storageStartDate).toISOString();
      } else {
        updates.storage_start_date = null;
      }

      if (storedAt) {
        updates.stored_at = new Date(storedAt).toISOString();
      } else {
        updates.stored_at = null;
      }

      const { data, error } = await supabase.functions.invoke('admin-update-box', {
        body: { boxId: box.id, updates },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      toast({
        title: "Box updated",
        description: `${box.box_number} has been updated successfully`,
      });

      onSaved();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error updating box:', error);
      toast({
        title: "Error updating box",
        description: error.message || "Failed to update box",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!box) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Box #{box.box_number}</DialogTitle>
          <DialogDescription>
            Make changes to this box's details.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Box Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter box name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Location Status</Label>
            <Select value={locationStatus} onValueChange={setLocationStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="home">At Home</SelectItem>
                <SelectItem value="pickup_scheduled">Pickup Scheduled</SelectItem>
                <SelectItem value="in_transit">In Transit</SelectItem>
                <SelectItem value="stored">Stored</SelectItem>
                <SelectItem value="returning">Returning</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="storageStartDate">Storage Start Date</Label>
            <Input
              id="storageStartDate"
              type="date"
              value={storageStartDate}
              onChange={(e) => setStorageStartDate(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">When billing started for this box</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="storedAt">Stored At Date</Label>
            <Input
              id="storedAt"
              type="date"
              value={storedAt}
              onChange={(e) => setStoredAt(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">When box arrived at facility</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
